// corporateExporter.ts — exportación XLSX con plantilla corporativa Axon.
// Usa ExcelJS (no SheetJS) para preservar el formato de la plantilla.
// Genera: Portada + Índice + una hoja por cada pestaña seleccionada.

import ExcelJS from "exceljs";
import type { NbSignal } from "./jsonContracts";

const BASE_URL = import.meta.env.BASE_URL;

export interface CorpTab {
  id:      string;
  title:   string;
  ied:     string;
  rows:    NbSignal[];
}

// ── Lógica de negocio: clasificar el TIPO de la señal ───────────────────────
function clasificarTipo(nombre: string): "ME FL" | "DC" | "DP" | "SP" {
  const t = nombre.toLowerCase();
  const esMedida = /\b(corriente|tensi[oó]n|voltaje|potencia|frecuencia|i[abc]\b|v(?:a|b|c|ab|bc|ca)\b|amperio|vatio|var\b|factor de potencia|hz)\b/.test(t);
  if (esMedida) return "ME FL";
  if (/\b(comando|mando|orden)\b/.test(t)) return "DC";
  if (/\b(posici[oó]n|estado de(?:l)? (?:interruptor|seccionador|cuchilla))\b/.test(t)) return "DP";
  return "SP";
}

// ── Lógica OFF / ON según el tipo ───────────────────────────────────────────
function offOn(tipo: string): [string, string] {
  switch (tipo) {
    case "ME FL": return ["", ""];
    case "DC":    return ["Abrir", "Cerrar"];
    case "DP":    return ["Abierto", "Cerrado"];
    default:      return ["Normal", "Falla"];   // SP
  }
}

// ── Clonar la hoja modelo conservando formato, merges y dimensiones ─────────
function cloneSheet(wb: ExcelJS.Workbook, src: ExcelJS.Worksheet, newName: string): ExcelJS.Worksheet {
  const ws = wb.addWorksheet(newName);
  src.columns.forEach((col, i) => {
    if (col && col.width) ws.getColumn(i + 1).width = col.width;
  });
  src.eachRow({ includeEmpty: true }, (row, rn) => {
    const newRow = ws.getRow(rn);
    newRow.height = row.height;
    row.eachCell({ includeEmpty: true }, (cell, cn) => {
      const nc = newRow.getCell(cn);
      nc.value = cell.value;
      if (cell.style) nc.style = JSON.parse(JSON.stringify(cell.style));
    });
    newRow.commit();
  });
  const merges: string[] = (src.model as any).merges || [];
  merges.forEach(m => { try { ws.mergeCells(m); } catch (e) { /* solapados */ } });
  return ws;
}

function sanitizeSheetName(name: string): string {
  // Excel: máx 31 chars, sin  : \ / ? * [ ]
  return name.replace(/[:\\/?*\[\]]/g, "_").substring(0, 31) || "Hoja";
}

/**
 * Exporta el archivo corporativo con las pestañas indicadas.
 * @param tabs      pestañas a incluir (en orden)
 * @param baseName  nombre del archivo de salida (sin extensión)
 */
export async function exportCorporateXlsx(tabs: CorpTab[], baseName: string): Promise<void> {
  // Cargar la plantilla embebida desde public/
  const resp = await fetch(`${BASE_URL}plantilla_corporativa.xlsx`);
  const buf  = await resp.arrayBuffer();
  const wb   = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);

  const tpl    = wb.getWorksheet("_SIGNAL_TPL_");
  const indice = wb.getWorksheet("índice");
  if (!tpl || !indice) throw new Error("Plantilla corporativa inválida (faltan hojas modelo)");

  // Evitar nombres de hoja duplicados
  const usados = new Set<string>(wb.worksheets.map(w => w.name));

  tabs.forEach((tab, idx) => {
    let nombre = sanitizeSheetName(tab.title);
    let n = nombre, k = 2;
    while (usados.has(n)) { n = sanitizeSheetName(`${nombre} (${k++})`); }
    usados.add(n);

    const ws = cloneSheet(wb, tpl, n);
    ws.getCell("A1").value = tab.title;   // fila 1 = nombre de la pestaña

    // Filas de datos desde la 12
    tab.rows.forEach((s, i) => {
      const r = 12 + i;
      const tipo = clasificarTipo(s.signal);
      const [off, on] = offOn(tipo);
      ws.getCell(`A${r}`).value = i + 1;        // Ítem
      ws.getCell(`B${r}`).value = "Internas";    // Borne origen
      ws.getCell(`C${r}`).value = "-";           // ENTR./SAL. BINARIA
      ws.getCell(`D${r}`).value = "";            // PRIO
      ws.getCell(`E${r}`).value = tab.ied;       // Equipo = IED
      ws.getCell(`F${r}`).value = "";            // Nombre corto
      ws.getCell(`G${r}`).value = s.signal;      // Nombre de señal
      ws.getCell(`H${r}`).value = tipo;          // TIPO
      ws.getCell(`I${r}`).value = off;           // OFF
      ws.getCell(`J${r}`).value = on;            // ON
      ws.getCell(`K${r}`).value = "";            // Alarma-Evento
      ws.getCell(`L${r}`).value = s.mmsNode;     // Maestro 61850 principal
      ws.getCell(`M${r}`).value = "";            // Maestro 61850 redundante
      ws.getCell(`N${r}`).value = "";            // Tipo (IEC104)
      ws.getCell(`O${r}`).value = "";            // IEC 104
      // P/Q/R (validación) se dejan como están en la plantilla
    });

    // Índice: una fila por pestaña desde la 11
    const idxRow = 11 + idx;
    indice.getCell(`A${idxRow}`).value = String(idx + 1).padStart(2, "0");
    indice.getCell(`B${idxRow}`).value = tab.title;
  });

  // Quitar la hoja modelo del resultado
  wb.removeWorksheet(tpl.id);

  // Saneamiento (fórmulas rotas, NaN, definedNames externos) + red de seguridad
  sanitizeWorkbook(wb);
  const rawBuf = await wb.xlsx.writeBuffer() as ArrayBuffer;
  const out = await stripInvalidNumbers(rawBuf);
  const blob = new Blob([out], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const safe = (baseName || "axon-tags").replace(/[<>:"/\\|?*\x00-\x1f]/g, "_");
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), { href: url, download: `${safe}.xlsx` });
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ═══════════════════ Formato propio del usuario ═══════════════════
// El usuario carga su propio libro XLSX. Requisitos mínimos de la(s) hoja(s)
// de listado: columnas "Equipo o IED", "Nombre de señal", "Tipo de dato" y
// "Nodo MMS" (en cualquier posición/fila de encabezado). Recomendado: hoja de
// portada y hoja "Índice" con columnas "Hoja" y "Descripción".

/** Lectura segura del texto de una celda: tolera celdas combinadas con master
 *  nulo (bug de ExcelJS: MergeValue.toString), richText, fórmulas y errores. */
function cellText(cell: ExcelJS.Cell): string {
  try { return cell.text ?? ""; } catch {
    const v = cell.value as unknown;
    if (v == null) return "";
    if (typeof v === "object") {
      const o = v as { richText?: { text: string }[]; text?: string; result?: unknown };
      if (o.richText) return o.richText.map(t => t.text).join("");
      if (typeof o.text === "string") return o.text;
      if (o.result != null && typeof o.result !== "object") return String(o.result);
      return "";
    }
    return String(v);
  }
}

function norm(s: unknown): string {
  return String(s ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
}

// Cada columna requerida admite alias: la primera entrada del grupo es la clave canónica.
const REQ_GROUPS: string[][] = [
  ["equipo o ied", "equipo", "ied"],
  ["nombre de señal"],
  ["tipo de dato"],
  ["nodo mms"],
].map(g => g.map(norm));
const K_IED  = REQ_GROUPS[0][0];
const K_NOM  = REQ_GROUPS[1][0];
const K_TIPO = REQ_GROUPS[2][0];
const K_NODO = REQ_GROUPS[3][0];

interface HeaderMap { row: number; cols: Record<string, number>; }

function findHeader(ws: ExcelJS.Worksheet, wanted: string[][], maxRows = 30): HeaderMap | null {
  for (let r = 1; r <= Math.min(maxRows, ws.rowCount || maxRows); r++) {
    const row = ws.getRow(r);
    const cols: Record<string, number> = {};
    row.eachCell({ includeEmpty: false }, (cell, cn) => {
      const v = norm(cellText(cell));
      for (const grupo of wanted) {
        if (grupo.includes(v) && cols[grupo[0]] === undefined) cols[grupo[0]] = cn;
      }
    });
    if (wanted.every(g => cols[g[0]] !== undefined)) return { row: r, cols };
  }
  return null;
}

/** Construye el libro con formato propio (retorna el buffer, sin descargar). */
// Neutraliza contenido que hace que Excel pida "recuperar": fórmulas con
// referencias a libros externos o #REF!, valores NaN/Infinity, y nombres
// definidos que apuntan a libros externos. Se aplica a AMBOS exportadores.
function sanitizeWorkbook(wb: ExcelJS.Workbook): void {
  for (const ws of wb.worksheets) {
    ws.eachRow({ includeEmpty: false }, row => {
      row.eachCell({ includeEmpty: false }, cell => {
        const v = cell.value as unknown;
        const esFormula = v != null && typeof v === "object" &&
          ("formula" in (v as object) || "sharedFormula" in (v as object));
        if (esFormula) {
          const o = v as { result?: unknown };
          let res = o.result;
          if (typeof res === "number" && !Number.isFinite(res)) res = null;
          if (typeof res === "object" && res !== null && "error" in (res as object)) res = null;
          try { cell.value = (res ?? null) as ExcelJS.CellValue; }
          catch { try { cell.value = null; } catch { /* celda combinada */ } }
          if ((res ?? null) === null) {
            try { const nf = cell.numFmt; if (nf && /[dmyhs]/i.test(nf)) cell.numFmt = "General"; }
            catch { /* mejor esfuerzo */ }
          }
        } else if (typeof v === "number" && !Number.isFinite(v)) {
          try { cell.value = null; } catch { /* celda combinada */ }
        } else if (typeof v === "string" && /^(NaN|-?Infinity)$/.test(v.trim())) {
          try { cell.value = null; } catch { /* celda combinada */ }
        }
      });
    });
  }
  const wbAny = wb as unknown as { definedNames?: { model?: unknown[] } };
  try {
    const dn = wbAny.definedNames;
    if (dn && Array.isArray(dn.model)) {
      dn.model = dn.model.filter((m) => {
        const ranges = (m as { ranges?: string[] }).ranges ?? [];
        return !ranges.some(r => /\[\d+\]/.test(r));
      });
    }
  } catch { /* mejor esfuerzo */ }
}

export async function buildCustomXlsx(templateBytes: ArrayBuffer, tabs: CorpTab[]): Promise<ArrayBuffer> {
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(templateBytes);

  // 1) Hoja de listado: la primera que tenga las 4 columnas mínimas
  let tplWs: ExcelJS.Worksheet | null = null;
  let hdr: HeaderMap | null = null;
  for (const ws of wb.worksheets) {
    const h = findHeader(ws, REQ_GROUPS);
    if (h) { tplWs = ws; hdr = h; break; }
  }
  if (!tplWs || !hdr) {
    throw new Error('El formato no contiene ninguna hoja con las columnas mínimas: "Equipo o IED" (o "Equipo" / "IED"), "Nombre de señal", "Tipo de dato" y "Nodo MMS"');
  }

  // 2) Hoja de índice (opcional): nombre "Índice/Indice" o cualquier hoja con "Hoja"+"Descripción"
  let idxWs: ExcelJS.Worksheet | null = null;
  let idxHdr: HeaderMap | null = null;
  for (const ws of wb.worksheets) {
    if (ws === tplWs) continue;
    const byName = ["indice"].includes(norm(ws.name));
    const h = findHeader(ws, [["hoja"], ["descripcion"]].map(g => g.map(norm)));
    if (h && (byName || !idxWs)) { idxWs = ws; idxHdr = h; if (byName) break; }
  }

  // Detección de encabezado de DOBLE fila: si la fila siguiente al encabezado
  // aún contiene rótulos (celdas de texto en columnas fuera de las de datos,
  // p. ej. "MODULO"/"IEC TYPE"), los datos empiezan una fila más abajo.
  const dataCols = new Set(Object.values(hdr.cols));
  const esFilaDeRotulos = (r: number): boolean => {
    const row = tplWs!.getRow(r);
    let rotulos = 0, enDatos = 0;
    row.eachCell({ includeEmpty: false }, (cell, cn) => {
      const t = cellText(cell).trim();
      if (!t) return;
      if (dataCols.has(cn)) enDatos++;
      else if (isNaN(Number(t))) rotulos++;   // texto no numérico fuera de columnas de datos
    });
    return rotulos > 0 && enDatos === 0;       // solo rótulos, ningún dato real
  };
  let dataRow0 = hdr.row + 1;
  if (esFilaDeRotulos(dataRow0)) dataRow0++;    // saltar 2ª fila del encabezado

  // Estilo de referencia: primera fila de datos REAL de la plantilla (con
  // contenido en la columna de nombre), para no heredar el relleno del
  // encabezado. Si no hay ninguna poblada, se deja sin estilo forzado.
  const estilos: Record<number, ExcelJS.Style> = {};
  let filaEstilo = -1;
  for (let r = dataRow0; r <= Math.min(tplWs.rowCount, dataRow0 + 200); r++) {
    const nomCell = tplWs.getRow(r).getCell(hdr.cols[K_NOM]);
    if (cellText(nomCell).trim()) { filaEstilo = r; break; }
  }
  if (filaEstilo > 0) {
    for (const cn of dataCols) {
      const st = tplWs.getRow(filaEstilo).getCell(cn).style;
      if (st) estilos[cn] = JSON.parse(JSON.stringify(st)) as ExcelJS.Style;
    }
  }

  const usados = new Set<string>(wb.worksheets.map(w => w.name));
  usados.delete(tplWs.name);
  const hojasCreadas: { sheet: string; tab: CorpTab }[] = [];

  tabs.forEach((tab) => {
    let nombre = sanitizeSheetName(tab.title);
    let n = nombre, k = 2;
    while (usados.has(n)) { n = sanitizeSheetName(`${nombre} (${k++})`); }
    usados.add(n);
    const ws = cloneSheet(wb, tplWs!, n);
    // Si la plantilla venía pre-llenada (formatos de proyectos reales), limpiar
    // los VALORES bajo el encabezado conservando estilos y estructura.
    for (let r = dataRow0; r <= ws.rowCount; r++) {
      ws.getRow(r).eachCell({ includeEmpty: false }, c => {
        try { c.value = null; } catch { /* celda combinada de solo lectura */ }
      });
    }
    tab.rows.forEach((s, i) => {
      const r = dataRow0 + i;
      const row = ws.getRow(r);
      const put = (key: string, val: string | number) => {
        const cn = hdr!.cols[key];
        const cell = row.getCell(cn);
        cell.value = val;
        // Aplicar el estilo de fila de datos a TODAS por igual (incluida la
        // primera), para no heredar el relleno azul del encabezado.
        if (estilos[cn]) cell.style = JSON.parse(JSON.stringify(estilos[cn])) as ExcelJS.Style;
      };
      put(K_IED,  tab.ied || s.iedRef || "");
      put(K_NOM,  s.signal);
      put(K_TIPO, clasificarTipo(s.signal));
      put(K_NODO, s.mmsNode);
      row.commit();
    });
    hojasCreadas.push({ sheet: n, tab });
  });

  // Retirar la hoja plantilla del resultado
  wb.removeWorksheet(tplWs.id);

  // 3) Índice: añadir una fila por hoja creada bajo el encabezado (tras la última con contenido)
  if (idxWs && idxHdr) {
    const cHoja = idxHdr.cols[norm("hoja")], cDesc = idxHdr.cols[norm("descripcion")];
    let r = idxHdr.row + 1;
    while (cellText(idxWs.getRow(r).getCell(cHoja)).trim() !== "") r++;
    const stH = idxWs.getRow(idxHdr.row + 1).getCell(cHoja).style;
    const stD = idxWs.getRow(idxHdr.row + 1).getCell(cDesc).style;
    for (const h of hojasCreadas) {
      const row = idxWs.getRow(r++);
      const a = row.getCell(cHoja);  a.value = h.sheet;
      const b = row.getCell(cDesc);  b.value = h.tab.ied ? `${h.tab.title} — ${h.tab.ied}` : h.tab.title;
      if (stH) a.style = JSON.parse(JSON.stringify(stH)) as ExcelJS.Style;
      if (stD) b.style = JSON.parse(JSON.stringify(stD)) as ExcelJS.Style;
      row.commit();
    }
  }

  // ── Saneamiento (fórmulas rotas, NaN, definedNames externos) ──
  sanitizeWorkbook(wb);

  const rawBuf = await wb.xlsx.writeBuffer() as ArrayBuffer;
  // Red de seguridad: por más que se limpien las fórmulas rotas, ExcelJS puede
  // reescribir <v>NaN</v> en celdas heredadas con formato de fecha. Excel las
  // rechaza ("recuperar contenido"). Se eliminan a nivel de XML como último paso.
  return await stripInvalidNumbers(rawBuf);
}

// Reescribe el .xlsx (ZIP) neutralizando celdas con valores numéricos inválidos
// (NaN / Infinity), que hacen que Excel pida recuperar el contenido.
async function stripInvalidNumbers(buf: ArrayBuffer): Promise<ArrayBuffer> {
  try {
    const JSZip = (await import("jszip")).default;
    const zip = await JSZip.loadAsync(buf);
    let cambios = 0;
    const tareas: Promise<void>[] = [];
    zip.forEach((path, file) => {
      if (!/^xl\/worksheets\/sheet\d+\.xml$/.test(path)) return;
      tareas.push((async () => {
        let xml = await file.async("string");
        if (!/(NaN|Infinity)/.test(xml)) return;
        // <c ...><v>NaN</v></c>  →  <c .../>  (celda vacía, conserva estilo por el atributo s)
        xml = xml.replace(/(<c\b[^>]*?)(\s*\/?>)(?:<f[^>]*>[^<]*<\/f>)?<v>(?:NaN|-?Infinity)<\/v><\/c>/g,
          (_m, open: string) => open.replace(/\/?$/, "") + "/>");
        // casos sueltos <v>NaN</v> sin cerrar limpio
        xml = xml.replace(/<v>(?:NaN|-?Infinity)<\/v>/g, "");
        zip.file(path, xml);
        cambios++;
      })());
    });
    await Promise.all(tareas);
    if (!cambios) return buf;
    const out = await zip.generateAsync({ type: "arraybuffer",
      compression: "DEFLATE", compressionOptions: { level: 6 } });
    return out;
  } catch {
    return buf;   // si algo falla, devolver el original (mejor que romper la descarga)
  }
}

/** Exporta con el formato propio del usuario y descarga. */
export async function exportCustomXlsx(templateBytes: ArrayBuffer, tabs: CorpTab[], baseName: string): Promise<void> {
  const out = await buildCustomXlsx(templateBytes, tabs);
  const blob = new Blob([out], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const safe = (baseName || "axon-tags").replace(/[<>:"/\\|?*\x00-\x1f]/g, "_");
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), { href: url, download: `${safe}.xlsx` });
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
